import { useState, useEffect, useMemo, useCallback } from 'react'
import { resellerService, resellerSaleService, resellerPayoutService } from '../services/AdminServices'
import {
  Users, Plus, Trash2, Save, X, Edit3, AlertTriangle,
  Eye, DollarSign, Copy, Check, TrendingUp, CreditCard,
  Search, ShoppingCart, Pen, Ticket, Info, Bell
} from 'lucide-react'
import './ResellersHub.css'

const TIERS = [
  { value: 'standard', label: 'Standard', color: '#94a3b8' },
  { value: 'silver', label: 'Silver', color: '#a8a29e' },
  { value: 'gold', label: 'Gold', color: '#f59e0b' },
  { value: 'platinum', label: 'Platinum', color: '#a78bfa' },
]

const STATUSES = [
  { value: 'pending', label: 'Pendiente', color: '#f59e0b' },
  { value: 'active', label: 'Activo', color: '#4ade80' },
  { value: 'suspended', label: 'Suspendido', color: '#f87171' },
  { value: 'inactive', label: 'Inactivo', color: '#94a3b8' },
]

const COMMISSION_RATE = 20 // Comisión fija del 20% en hardware
const DEFAULT_RATES = { 'GP-BOX': 20, 'GP-MINI': 20, 'GP-CAM': 20, 'GP-HIDRO': 20, 'GP-RELAY': 20, 'GP-DIM': 20, default: 20 }

function formatCurrency(n) {
  if (!n) return '$0'
  return '$' + Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function getStatus(s) { return STATUSES.find(st => st.value === s) || STATUSES[0] }
function getTier(t) { return TIERS.find(ti => ti.value === t) || TIERS[0] }
function getInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

export default function ResellersHub() {
  // ─── State ────────────────────────────────────────────
  const [resellers, setResellers] = useState([])
  const [sales, setSales] = useState([])
  const [payouts, setPayouts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('resellers') // resellers | sales | payouts | detail
  const [detailReseller, setDetailReseller] = useState(null)
  const [detailSales, setDetailSales] = useState([])
  const [detailPayouts, setDetailPayouts] = useState([])
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [copiedCode, setCopiedCode] = useState(null)

  // Form state
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({
    name: '', email: '', phone: '', businessName: '', tier: 'standard',
    notes: '', cbu: '', alias: '', mpPayerEmail: '', code: ''
  })

  // Payout form
  const [showPayoutForm, setShowPayoutForm] = useState(false)
  const [payoutForm, setPayoutForm] = useState({
    resellerId: '', amount: '', method: 'transfer', notes: '', salesIncluded: []
  })

  // ─── Load data ────────────────────────────────────────
  const loadResellers = useCallback(async () => {
    try {
      setLoading(true)
      const res = await resellerService.list()
      setResellers(res.resellers || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadSales = useCallback(async () => {
    try {
      const res = await resellerSaleService.list()
      setSales(res.sales || [])
    } catch (e) { setError(e.message) }
  }, [])

  const loadPayouts = useCallback(async () => {
    try {
      const res = await resellerPayoutService.list()
      setPayouts(res.payouts || [])
    } catch (e) { setError(e.message) }
  }, [])

  useEffect(() => {
    loadResellers()
    loadSales()
    loadPayouts()
  }, [loadResellers, loadSales, loadPayouts])

  // ─── Stats ────────────────────────────────────────────
  const stats = useMemo(() => {
    const active = resellers.filter(r => r.status === 'active').length
    const pending = resellers.filter(r => r.status === 'pending').length
    const totalRevenue = resellers.reduce((sum, r) => sum + (r.totalRevenue || 0), 0)
    const totalCommission = resellers.reduce((sum, r) => sum + (r.totalCommission || 0), 0)
    const pendingCommission = resellers.reduce((sum, r) => sum + (r.pendingCommission || 0), 0)
    const totalSales = resellers.reduce((sum, r) => sum + (r.totalSales || 0), 0)
    return { total: resellers.length, active, pending, totalRevenue, totalCommission, pendingCommission, totalSales }
  }, [resellers])

  // ─── Filtered list ────────────────────────────────────
  const filteredResellers = useMemo(() => {
    let list = resellers
    if (filterStatus) list = list.filter(r => r.status === filterStatus)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.code || '').toLowerCase().includes(q) ||
        (r.businessName || '').toLowerCase().includes(q)
      )
    }
    return list
  }, [resellers, filterStatus, search])

  // ─── Reseller form ───────────────────────────────────
  function openForm(reseller) {
    if (reseller) {
      setEditingId(reseller.$id)
      setForm({
        name: reseller.name || '', email: reseller.email || '', phone: reseller.phone || '',
        businessName: reseller.businessName || '', tier: reseller.tier || 'standard',
        notes: reseller.notes || '', cbu: reseller.cbu || '', alias: reseller.alias || '',
        mpPayerEmail: reseller.mpPayerEmail || '', code: reseller.code || ''
      })
    } else {
      setEditingId(null)
      setForm({
        name: '', email: '', phone: '', businessName: '', tier: 'standard',
        notes: '', cbu: '', alias: '', mpPayerEmail: '', code: ''
      })
    }
    setShowForm(true)
  }

  async function saveReseller() {
    if (!form.name.trim() || !form.email.trim()) { setError('Nombre y email son requeridos'); return }
    try {
      if (editingId) await resellerService.update(editingId, form)
      else await resellerService.create(form)
      setShowForm(false)
      setEditingId(null)
      loadResellers()
    } catch (e) { setError(e.message) }
  }

  async function deleteReseller(id) {
    if (!confirm('¿Eliminar este revendedor? Se perderá todo el historial.')) return
    try { await resellerService.delete(id); loadResellers() } catch (e) { setError(e.message) }
  }

  async function toggleStatus(reseller) {
    const newStatus = reseller.status === 'active' ? 'suspended' : 'active'
    try {
      await resellerService.update(reseller.$id, { status: newStatus })
      loadResellers()
    } catch (e) { setError(e.message) }
  }

  // ─── Detail view ──────────────────────────────────────
  async function openDetail(reseller) {
    try {
      const res = await resellerService.get(reseller.$id)
      setDetailReseller(res.reseller)
      setDetailSales(res.sales || [])
      setDetailPayouts(res.payouts || [])
      setTab('detail')
    } catch (e) { setError(e.message) }
  }

  // ─── Payout form ──────────────────────────────────────
  function openPayoutForm(resellerId) {
    const pendingSales = sales.filter(s => s.resellerId === resellerId && s.commissionStatus === 'pending')
    const totalPending = pendingSales.reduce((sum, s) => sum + (s.totalCommission || 0), 0)
    setPayoutForm({
      resellerId, amount: totalPending, method: 'transfer', notes: '',
      salesIncluded: pendingSales.map(s => s.$id)
    })
    setShowPayoutForm(true)
  }

  async function savePayout() {
    if (!payoutForm.resellerId || !payoutForm.amount) { setError('Revendedor y monto son requeridos'); return }
    try {
      await resellerPayoutService.create({
        ...payoutForm, amount: Number(payoutForm.amount)
      })
      setShowPayoutForm(false)
      loadPayouts()
      loadSales()
      loadResellers()
    } catch (e) { setError(e.message) }
  }

  async function completePayout(payoutId) {
    if (!confirm('¿Confirmar que se realizó el pago?')) return
    try {
      await resellerPayoutService.update(payoutId, { status: 'completed' })
      loadPayouts()
      loadResellers()
      loadSales()
    } catch (e) { setError(e.message) }
  }

  // ─── Copy code ────────────────────────────────────────
  function copyCode(code) {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  // ─── Loading ──────────────────────────────────────────
  if (loading && resellers.length === 0) {
    return <div className="rsl-page"><div className="rsl-loading"><div className="rsl-spinner" /> Cargando revendedores...</div></div>
  }

  return (
    <div className="rsl-page">
      {/* Header */}
      <div className="rsl-header">
        <div className="rsl-header-left">
          <Users size={28} />
          <div>
            <h1>Programa de Revendedores</h1>
            <p className="rsl-subtitle">{stats.active} activos · {stats.totalSales} ventas · {formatCurrency(stats.pendingCommission)} pendiente</p>
          </div>
        </div>
        <div className="rsl-header-actions">
          <button className="rsl-btn rsl-btn-primary" onClick={() => openForm()}>
            <Plus size={16} /> Nuevo Revendedor
          </button>
        </div>
      </div>

      {error && <div className="rsl-error"><AlertTriangle size={16} /> {error} <button onClick={() => setError(null)}><X size={14} /></button></div>}

      {/* Stats */}
      <div className="rsl-stats">
        <div className="rsl-stat">
          <span className="rsl-stat-num">{stats.total}</span>
          <span className="rsl-stat-label">Revendedores</span>
        </div>
        <div className="rsl-stat">
          <span className="rsl-stat-num" style={{ color: '#4ade80' }}>{stats.active}</span>
          <span className="rsl-stat-label">Activos</span>
        </div>
        <div className="rsl-stat">
          <span className="rsl-stat-num" style={{ color: '#60a5fa' }}>{formatCurrency(stats.totalRevenue)}</span>
          <span className="rsl-stat-label">Revenue Total</span>
        </div>
        <div className="rsl-stat">
          <span className="rsl-stat-num" style={{ color: '#a78bfa' }}>{formatCurrency(stats.totalCommission)}</span>
          <span className="rsl-stat-label">Comisiones Total</span>
        </div>
        <div className="rsl-stat">
          <span className="rsl-stat-num" style={{ color: '#f59e0b' }}>{formatCurrency(stats.pendingCommission)}</span>
          <span className="rsl-stat-label">Pendiente de Pago</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="rsl-tabs">
        <button className={tab === 'resellers' ? 'active' : ''} onClick={() => setTab('resellers')}>
          <Users size={16} /> Revendedores
        </button>
        <button className={tab === 'sales' ? 'active' : ''} onClick={() => setTab('sales')}>
          <TrendingUp size={16} /> Ventas
        </button>
        <button className={tab === 'payouts' ? 'active' : ''} onClick={() => setTab('payouts')}>
          <CreditCard size={16} /> Liquidaciones
        </button>
      </div>

      {/* ───── RESELLERS TAB ───── */}
      {tab === 'resellers' && (
        <>
          <div className="rsl-filters">
            <div className="rsl-search">
              <Search size={16} />
              <input placeholder="Buscar por nombre, email o código..." value={search}
                onChange={e => setSearch(e.target.value)} />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Todos los estados</option>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {stats.pending > 0 && (
            <div className="rsl-pending-banner">
              <Bell size={20} />
              <div className="rsl-pending-banner-text">
                <strong>{stats.pending} {stats.pending === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'} de aprobación</strong>
                <span>Revisá y aprobá las solicitudes de nuevos revendedores</span>
              </div>
              <span className="rsl-pending-count">{stats.pending}</span>
              <button className="rsl-btn rsl-btn-sm rsl-btn-primary" onClick={() => { setFilterStatus('pending') }}>
                Ver pendientes
              </button>
            </div>
          )}

          <div className="rsl-table-container">
            <table className="rsl-table">
              <thead>
                <tr>
                  <th>Revendedor</th><th>Código</th><th>Tier</th><th>Estado</th>
                  <th>Ventas</th><th>Revenue</th><th>Comisión Pend.</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredResellers.length === 0 ? (
                  <tr><td colSpan={8} className="rsl-empty">No hay revendedores</td></tr>
                ) : filteredResellers.map(r => {
                  const status = getStatus(r.status)
                  const tier = getTier(r.tier)
                  return (
                    <tr key={r.$id}>
                      <td>
                        <div className="rsl-reseller-cell">
                          <div className={`rsl-avatar rsl-avatar-${r.status || 'inactive'}`}>
                            {getInitials(r.name)}
                          </div>
                          <div className="rsl-cell-name">
                            <strong>{r.name}</strong>
                            <span>{r.email}</span>
                            {r.businessName && <span className="rsl-business">{r.businessName}</span>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <button className="rsl-code-btn" onClick={() => copyCode(r.code)} title="Copiar código">
                          <code>{r.code}</code>
                          {copiedCode === r.code ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                      </td>
                      <td><span className={`rsl-tier-badge rsl-tier-${r.tier || 'standard'}`}>{tier.label}</span></td>
                      <td>
                        <span className={`rsl-status-badge rsl-status-${r.status || 'pending'}`}>
                          {status.label}
                        </span>
                      </td>
                      <td>{r.totalSales || 0}</td>
                      <td>{formatCurrency(r.totalRevenue)}</td>
                      <td style={{ color: r.pendingCommission > 0 ? '#f59e0b' : 'inherit' }}>
                        {formatCurrency(r.pendingCommission)}
                      </td>
                      <td className="rsl-actions-cell">
                        <button onClick={() => openDetail(r)} title="Ver detalle"><Eye size={16} /></button>
                        <button onClick={() => openForm(r)} title="Editar"><Edit3 size={16} /></button>
                        <button onClick={() => toggleStatus(r)} title={r.status === 'active' ? 'Suspender' : 'Activar'}
                          className={r.status === 'active' ? 'rsl-btn-warn' : 'rsl-btn-ok'}>
                          {r.status === 'active' ? <X size={16} /> : <Check size={16} />}
                        </button>
                        {r.pendingCommission > 0 && (
                          <button onClick={() => openPayoutForm(r.$id)} title="Liquidar comisiones" className="rsl-btn-ok">
                            <DollarSign size={16} />
                          </button>
                        )}
                        <button onClick={() => deleteReseller(r.$id)} title="Eliminar" className="rsl-btn-danger"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ───── SALES TAB ───── */}
      {tab === 'sales' && (
        <div className="rsl-table-container">
          <table className="rsl-table">
            <thead>
              <tr>
                <th>Fecha</th><th>Revendedor</th><th>Origen</th><th>Cliente</th><th>Productos</th>
                <th>Monto</th><th>Comisión</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 ? (
                <tr><td colSpan={8} className="rsl-empty">No hay ventas registradas</td></tr>
              ) : sales.map(s => {
                const isWC = s.source === 'wc-webhook' || s.type === 'woocommerce'
                const statusColor = s.commissionStatus === 'paid' ? '#4ade80' : s.commissionStatus === 'verified' ? '#60a5fa' : s.commissionStatus === 'approved' ? '#60a5fa' : '#f59e0b'
                const statusLabel = s.commissionStatus === 'paid' ? 'Pagada' : s.commissionStatus === 'verified' ? 'Verificada' : s.commissionStatus === 'approved' ? 'Aprobada' : 'Pendiente'
                return (
                  <tr key={s.$id}>
                    <td>{formatDate(s.saleDate || s.createdAt)}</td>
                    <td>
                      <div className="rsl-cell-name">
                        <strong>{s.resellerName}</strong>
                        <code>{s.resellerCode}</code>
                      </div>
                    </td>
                    <td>
                      <span className={`rsl-source-badge ${isWC ? 'rsl-source-wc' : 'rsl-source-manual'}`}>
                        {isWC ? <><ShoppingCart size={12} /> WooCommerce</> : <><Pen size={12} /> Manual</>}
                      </span>
                    </td>
                    <td>{s.customerName || '—'}</td>
                    <td>
                      {(s.products || []).map((p, i) => (
                        <span key={i} className="rsl-product-tag">{p.qty || 1}x {p.model}</span>
                      ))}
                    </td>
                    <td>{formatCurrency(s.saleAmount || s.totalAmount)}</td>
                    <td>{formatCurrency(s.commissionAmount || s.totalCommission)}</td>
                    <td>
                      <span className="rsl-status-badge" style={{ color: statusColor, borderColor: statusColor }}>
                        {statusLabel}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ───── PAYOUTS TAB ───── */}
      {tab === 'payouts' && (
        <div className="rsl-table-container">
          <table className="rsl-table">
            <thead>
              <tr>
                <th>Período</th><th>Revendedor</th><th>Monto</th><th>Método</th>
                <th>Ventas</th><th>Estado</th><th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr><td colSpan={7} className="rsl-empty">No hay liquidaciones</td></tr>
              ) : payouts.map(p => (
                <tr key={p.$id}>
                  <td>{p.period || formatDate(p.createdAt)}</td>
                  <td>
                    <div className="rsl-cell-name">
                      <strong>{p.resellerName}</strong>
                      <code>{p.resellerCode}</code>
                    </div>
                  </td>
                  <td className="rsl-payout-amount">{formatCurrency(p.totalCommission || p.amount)}</td>
                  <td>{p.method === 'transfer' ? 'Transferencia' : p.method === 'mercadopago' ? 'MercadoPago' : p.method || 'Auto'}</td>
                  <td>{p.salesCount || (p.salesIncluded || []).length || (p.salesDetail || []).length}</td>
                  <td>
                    <span className="rsl-status-badge" style={{
                      color: p.status === 'completed' ? '#4ade80' : '#f59e0b',
                      borderColor: p.status === 'completed' ? '#4ade80' : '#f59e0b'
                    }}>
                      {p.status === 'completed' ? 'Completada' : 'Pendiente'}
                    </span>
                  </td>
                  <td>
                    {p.status === 'pending' && (
                      <button className="rsl-btn rsl-btn-sm rsl-btn-primary" onClick={() => completePayout(p.$id)}>
                        <Check size={14} /> Confirmar Pago
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ───── DETAIL VIEW ───── */}
      {tab === 'detail' && detailReseller && (
        <div className="rsl-detail">
          <button className="rsl-back-btn" onClick={() => setTab('resellers')}>
            ← Volver a lista
          </button>

          <div className="rsl-detail-header">
            <div>
              <h2>{detailReseller.name}</h2>
              <p>{detailReseller.email} {detailReseller.phone && `· ${detailReseller.phone}`}</p>
              {detailReseller.businessName && <p className="rsl-business">{detailReseller.businessName}</p>}
            </div>
            <div className="rsl-detail-badges">
              <span className={`rsl-tier-badge rsl-tier-${detailReseller.tier || 'standard'}`}>
                {getTier(detailReseller.tier).label}
              </span>
              <span className={`rsl-status-badge rsl-status-${detailReseller.status || 'pending'}`}>
                {getStatus(detailReseller.status).label}
              </span>
            </div>
          </div>

          <div className="rsl-detail-code">
            <label>Código de revendedor / Cupón WooCommerce</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="rsl-code-btn rsl-code-lg" onClick={() => copyCode(detailReseller.code)}>
                <code>{detailReseller.code}</code>
                {copiedCode === detailReseller.code ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <span className="rsl-wc-badge"><ShoppingCart size={14} /> Cupón activo en tienda</span>
            </div>
            <span className="rsl-code-hint">Link: growpro.club/r/{detailReseller.code} · Cupón WC: {(detailReseller.code || '').toLowerCase()}</span>
          </div>

          <div className="rsl-detail-stats">
            <div className="rsl-stat">
              <span className="rsl-stat-num">{detailReseller.totalSales || 0}</span>
              <span className="rsl-stat-label">Ventas</span>
            </div>
            <div className="rsl-stat">
              <span className="rsl-stat-num" style={{ color: '#60a5fa' }}>{formatCurrency(detailReseller.totalRevenue)}</span>
              <span className="rsl-stat-label">Revenue</span>
            </div>
            <div className="rsl-stat">
              <span className="rsl-stat-num" style={{ color: '#a78bfa' }}>{formatCurrency(detailReseller.totalCommission)}</span>
              <span className="rsl-stat-label">Comisión Total</span>
            </div>
            <div className="rsl-stat">
              <span className="rsl-stat-num" style={{ color: '#f59e0b' }}>{formatCurrency(detailReseller.pendingCommission)}</span>
              <span className="rsl-stat-label">Pendiente</span>
            </div>
            <div className="rsl-stat">
              <span className="rsl-stat-num" style={{ color: '#4ade80' }}>{formatCurrency(detailReseller.paidCommission)}</span>
              <span className="rsl-stat-label">Pagado</span>
            </div>
          </div>

          {/* Commission rate + WC coupon */}
          <div className="rsl-detail-section">
            <h3>Comisión y Cupón</h3>
            <div className="rsl-rates">
              <div className="rsl-rate-item">
                <span>Hardware</span>
                <span className="rsl-rate-pct">{COMMISSION_RATE}%</span>
              </div>
              <div className="rsl-rate-item">
                <span>Descuento cupón</span>
                <span className="rsl-rate-pct" style={{ color: '#4ade80' }}>5%</span>
              </div>
              <div className="rsl-rate-item rsl-rate-wide">
                <span>Cupón WooCommerce</span>
                <span className="rsl-rate-coupon">
                  <Ticket size={14} /> {(detailReseller.code || '').toLowerCase()}
                </span>
                <span className="rsl-rate-hint">Se crea automáticamente al activar</span>
              </div>
            </div>
          </div>

          {/* Payment info */}
          {(detailReseller.cbu || detailReseller.alias || detailReseller.mpPayerEmail) && (
            <div className="rsl-detail-section">
              <h3>Datos de Pago</h3>
              <div className="rsl-payment-info">
                {detailReseller.cbu && <div><label>CBU:</label> <span>{detailReseller.cbu}</span></div>}
                {detailReseller.alias && <div><label>Alias:</label> <span>{detailReseller.alias}</span></div>}
                {detailReseller.mpPayerEmail && <div><label>MercadoPago:</label> <span>{detailReseller.mpPayerEmail}</span></div>}
              </div>
            </div>
          )}

          {/* Recent sales */}
          <div className="rsl-detail-section">
            <div className="rsl-section-header">
              <h3>Últimas Ventas</h3>
            </div>
            <div className="rsl-auto-info">
              <Info size={14} />
              <span>Las ventas se registran automáticamente cuando un cliente usa el cupón <strong>{(detailReseller.code || '').toLowerCase()}</strong> en la tienda WooCommerce.</span>
            </div>
            {detailSales.length === 0 ? (
              <p className="rsl-empty-text">Sin ventas registradas</p>
            ) : (
              <table className="rsl-table rsl-table-sm">
                <thead>
                  <tr><th>Fecha</th><th>Cliente</th><th>Productos</th><th>Monto</th><th>Comisión</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {detailSales.slice(0, 10).map(s => (
                    <tr key={s.$id}>
                      <td>{formatDate(s.createdAt)}</td>
                      <td>{s.customerName || '—'}</td>
                      <td>{(s.products || []).map((p, i) => <span key={i} className="rsl-product-tag">{p.qty}x {p.model}</span>)}</td>
                      <td>{formatCurrency(s.saleAmount || s.totalAmount)}</td>
                      <td>{formatCurrency(s.commissionAmount || s.totalCommission)}</td>
                      <td>
                        {(() => {
                          const sc = s.commissionStatus === 'paid' ? '#4ade80' : s.commissionStatus === 'verified' ? '#60a5fa' : '#f59e0b'; return (
                            <span className="rsl-status-badge" style={{ color: sc, borderColor: sc }}>
                              {s.commissionStatus === 'paid' ? 'Pagada' : s.commissionStatus === 'verified' ? 'Verificada' : 'Pendiente'}
                            </span>
                          )
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Payouts */}
          <div className="rsl-detail-section">
            <div className="rsl-section-header">
              <h3>Liquidaciones</h3>
              {detailReseller.pendingCommission > 0 && (
                <button className="rsl-btn rsl-btn-sm rsl-btn-primary" onClick={() => openPayoutForm(detailReseller.$id)}>
                  <DollarSign size={14} /> Liquidar {formatCurrency(detailReseller.pendingCommission)}
                </button>
              )}
            </div>
            {detailPayouts.length === 0 ? (
              <p className="rsl-empty-text">Sin liquidaciones</p>
            ) : (
              <table className="rsl-table rsl-table-sm">
                <thead>
                  <tr><th>Fecha</th><th>Monto</th><th>Método</th><th>Estado</th><th>Acciones</th></tr>
                </thead>
                <tbody>
                  {detailPayouts.map(p => (
                    <tr key={p.$id}>
                      <td>{formatDate(p.createdAt)}</td>
                      <td className="rsl-payout-amount">{formatCurrency(p.amount)}</td>
                      <td>{p.method === 'transfer' ? 'Transferencia' : 'MercadoPago'}</td>
                      <td>
                        <span className="rsl-status-badge" style={{
                          color: p.status === 'completed' ? '#4ade80' : '#f59e0b',
                          borderColor: p.status === 'completed' ? '#4ade80' : '#f59e0b'
                        }}>
                          {p.status === 'completed' ? 'Completada' : 'Pendiente'}
                        </span>
                      </td>
                      <td>
                        {p.status === 'pending' && (
                          <button className="rsl-btn rsl-btn-sm rsl-btn-primary" onClick={() => completePayout(p.$id)}>
                            <Check size={14} /> Confirmar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ───── RESELLER FORM (MODAL) ───── */}
      {showForm && (
        <div className="rsl-form-overlay">
          <div className="rsl-form">
            <div className="rsl-form-header">
              <h2>{editingId ? 'Editar Revendedor' : 'Nuevo Revendedor'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="rsl-form-body">
              <div className="rsl-form-row-2col">
                <div className="rsl-form-row">
                  <label>Nombre *</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Nombre completo" />
                </div>
                <div className="rsl-form-row">
                  <label>Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="email@ejemplo.com" />
                </div>
              </div>
              <div className="rsl-form-row-2col">
                <div className="rsl-form-row">
                  <label>Teléfono</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="+54 11 1234 5678" />
                </div>
                <div className="rsl-form-row">
                  <label>Negocio / Growshop</label>
                  <input value={form.businessName} onChange={e => setForm({ ...form, businessName: e.target.value })}
                    placeholder="Nombre del negocio" />
                </div>
              </div>
              <div className="rsl-form-row">
                <label>Tier</label>
                <select value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })}>
                  {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div className="rsl-form-row">
                <label>Código / Cupón {editingId ? '' : '(opcional — se genera automáticamente si se deja vacío)'}</label>
                <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                  placeholder={editingId ? 'Código actual' : 'Ej: GROWSHOP10'} maxLength={20} />
                {editingId && <span className="rsl-form-hint">Al cambiar el código se actualiza el cupón en WooCommerce automáticamente</span>}
              </div>

              <div className="rsl-form-commission-info">
                <Ticket size={16} />
                <span>Comisión fija: <strong>{COMMISSION_RATE}%</strong> en todo el hardware · Cupón de 5% se crea automáticamente en WooCommerce al activar</span>
              </div>

              <div className="rsl-form-section-title">Datos de Pago</div>
              <div className="rsl-form-row-2col">
                <div className="rsl-form-row">
                  <label>CBU</label>
                  <input value={form.cbu} onChange={e => setForm({ ...form, cbu: e.target.value })}
                    placeholder="CBU para transferencias" />
                </div>
                <div className="rsl-form-row">
                  <label>Alias</label>
                  <input value={form.alias} onChange={e => setForm({ ...form, alias: e.target.value })}
                    placeholder="Alias bancario" />
                </div>
              </div>
              <div className="rsl-form-row">
                <label>Email MercadoPago</label>
                <input value={form.mpPayerEmail} onChange={e => setForm({ ...form, mpPayerEmail: e.target.value })}
                  placeholder="Email de cuenta MP" />
              </div>

              <div className="rsl-form-row">
                <label>Notas</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Notas internas..." rows={3} />
              </div>
            </div>
            <div className="rsl-form-footer">
              <button className="rsl-btn rsl-btn-secondary" onClick={() => setShowForm(false)}>
                <X size={16} /> Cancelar
              </button>
              <button className="rsl-btn rsl-btn-primary" onClick={saveReseller}>
                <Save size={16} /> {editingId ? 'Guardar' : 'Crear Revendedor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───── PAYOUT FORM (MODAL) ───── */}
      {showPayoutForm && (
        <div className="rsl-form-overlay">
          <div className="rsl-form rsl-form-sm">
            <div className="rsl-form-header">
              <h2>Liquidar Comisiones</h2>
              <button onClick={() => setShowPayoutForm(false)}><X size={20} /></button>
            </div>
            <div className="rsl-form-body">
              <div className="rsl-form-row">
                <label>Monto a liquidar</label>
                <input type="number" value={payoutForm.amount}
                  onChange={e => setPayoutForm({ ...payoutForm, amount: e.target.value })} />
              </div>
              <div className="rsl-form-row">
                <label>Método de pago</label>
                <select value={payoutForm.method}
                  onChange={e => setPayoutForm({ ...payoutForm, method: e.target.value })}>
                  <option value="transfer">Transferencia bancaria</option>
                  <option value="mercadopago">MercadoPago</option>
                </select>
              </div>
              <div className="rsl-form-row">
                <label>Ventas incluidas: {payoutForm.salesIncluded.length}</label>
              </div>
              <div className="rsl-form-row">
                <label>Notas</label>
                <textarea value={payoutForm.notes}
                  onChange={e => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                  placeholder="Referencia de transferencia, etc." rows={2} />
              </div>
            </div>
            <div className="rsl-form-footer">
              <button className="rsl-btn rsl-btn-secondary" onClick={() => setShowPayoutForm(false)}>
                <X size={16} /> Cancelar
              </button>
              <button className="rsl-btn rsl-btn-primary" onClick={savePayout}>
                <DollarSign size={16} /> Crear Liquidación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
